import React, { useEffect, useState, useRef } from "react";
import { Helmet } from "react-helmet";
import { ArrowUpRight } from "iconoir-react";
import { Play } from "lucide-react";
import {
  FaCheckCircle,
  FaUsers,
  FaCogs,
  FaChartLine,
  FaGraduationCap,
  FaBookOpen,
  FaUserTie,
  FaSchool,
  FaBullseye,
  FaHandshake,
  FaCloud,
  FaTrophy,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaLandmark,
  FaChevronLeft,
  FaChevronRight,
  FaSearchPlus,
  FaTimes,
} from "react-icons/fa";
import AOS from "aos";
import "aos/dist/aos.css";

import Header from "../components/Header";
import Footer from "../components/Footer";
import FeaturedIn from "../components/FeaturedIn";
import "./Incubation.scss";

// ---- Hero / contextual photos (now: IIIT Delhi orientation event) ----
import sharkTankPhoto from "../assets/incubation/img-009.jpg";

// ---- Board member photos ----
import bmVikramJoshi from "../assets/incubation/img-044.png";
import bmGcBadoni from "../assets/incubation/img-045.png";
import bmSudhaPainuli from "../assets/incubation/img-037.png";
import bmShraddhaSharma from "../assets/incubation/img-036.png";
import bmAarushiJain from "../assets/incubation/img-038.png";
import bmShiveshSharma from "../assets/incubation/img-047.jpg";
import bmShrawanShukla from "../assets/incubation/img-046.png";
import bmDeepJoshi from "../assets/incubation/img-048.png";

// ---- Key Projects photos ----
import kpKvs from "../assets/incubation/img-080.jpg";
import kpHackathon from "../assets/incubation/img-081.png";
import kpNavodaya from "../assets/incubation/img-082.png";
import kpBeo from "../assets/incubation/img-083.jpg";
import kpSkill from "../assets/incubation/img-084.jpg";

// ---- Awards ----
import awardSocialImpact from "../assets/incubation/img-090.jpg";
import awardRuralSkill from "../assets/incubation/img-089.jpg";
import awardMnnit from "../assets/incubation/img-091.jpg";

// ---- Partner logos ----
import logoIhfc from "../assets/incubation/logo-ihfc.svg";
import logoMnnit from "../assets/incubation/logo-mnnit.png";
import logoPmshri from "../assets/incubation/logo-pmshri.png";
import logoKvs from "../assets/incubation/logo-kvs.png";
import logoNavodaya from "../assets/incubation/logo-navodaya.png";
import logoEklavya from "../assets/incubation/logo-emrs.jpg";
import logoSamagra from "../assets/incubation/logo-samagra.png";
import partnerScert from "../assets/partners/l-scert_11zon.webp";
import partnerSiemt from "../assets/partners/l-siemt_11zon.webp";
import partnerDosw from "../assets/partners/l-DoSW.webp";

// ---- Reel covers ----
import reelCover1 from "../assets/reel-cover-1.jpg";
import reelCover2 from "../assets/reel-cover-2.jpg";

// ---- SIP Orientation Program @ IIIT Delhi ----
import sip1  from "../assets/key-offerings/sip-1.jpg";
import sip2  from "../assets/key-offerings/sip-2.jpg";
import sip3  from "../assets/key-offerings/sip-3.jpg";
import sip4  from "../assets/key-offerings/sip-4.jpg";
import sip5  from "../assets/key-offerings/sip-5.jpg";
import sip6  from "../assets/key-offerings/sip-6.jpg";
import sip7  from "../assets/key-offerings/sip-7.jpg";
import sip8  from "../assets/key-offerings/sip-8.jpg";
import sip9  from "../assets/key-offerings/sip-9.jpg";
import sip10 from "../assets/key-offerings/sip-10.jpg";
import sip11 from "../assets/key-offerings/sip-11.jpg";
import sip12 from "../assets/key-offerings/sip-12.jpg";
import sip13 from "../assets/key-offerings/sip-13.jpg";
import sip14 from "../assets/key-offerings/sip-14.jpg";
import sip15 from "../assets/key-offerings/sip-15.jpg";
import sip16 from "../assets/key-offerings/sip-16.jpg";
import sip17 from "../assets/key-offerings/sip-17.jpg";
import AsOfNote from "../components/AsOfNote";
import { impact } from "../data/stats";

// Gallery — clean 3×4 grid right below the hero
const orientationPhotos = [
  sip2, sip3, sip4, sip5, sip6, sip7,
  sip8, sip9, sip10, sip11, sip12, sip13,
];

// Section / hero visuals — sourced from the IIIT Delhi orientation event
const heroPhoto            = sip1;
const muralPhoto           = sip14;
const auditoriumPhoto      = sip15;
const iitBombayPhoto       = sip16;
const schoolStudentsPhoto  = sip17;
const boardroomPhoto       = sip11;
const rashtrapatiKidsPhoto = sip8;


// ─── Data ────────────────────────────────────────────────────────────────────

const boardMembers = [
  { name: "Mr. Vikram Joshi",    role: "Academic Advisor",  org: "Ex- Joint Commissioner, NVS", photo: bmVikramJoshi },
  { name: "Dr. GC Badoni",       role: "Academic Advisor",  org: "Founding Member, EMRS",        photo: bmGcBadoni },
  { name: "Ms. Sudha Painuli",   role: "Academic Advisor",  org: "SCERT UK",                     photo: bmSudhaPainuli },
  { name: "Ms. Shraddha Sharma", role: "Strategic Advisor", org: "YourStory",                    photo: bmShraddhaSharma },
  { name: "Ms. Aarushi Jain",    role: "Senior Advisor",    org: "Microsoft",                    photo: bmAarushiJain },
  { name: "Prof. Shivesh Sharma",role: "Dean R&C",          org: "MNNIT Allahabad",              photo: bmShiveshSharma },
  { name: "Mr. Shrawan Shukla",  role: "Academic Advisor",  org: "Bharatiya Siksha Board",       photo: bmShrawanShukla },
  { name: "Dr. Deep Joshi",      role: "Senior Advisor",    org: "Former Member DST",            photo: bmDeepJoshi },
];

const programSteps = [
  { title: "Selection",    icon: <FaCheckCircle />, desc: "Schools are evaluated and selected through a rigorous fit assessment." },
  { title: "Onboarding",   icon: <FaHandshake />,   desc: "Leadership and faculty are aligned on goals, timelines, and roles." },
  { title: "System Setup", icon: <FaCloud />,        desc: "Academic, operational, and digital systems are deployed end-to-end." },
  { title: "Execution",    icon: <FaCogs />,         desc: "Day-to-day implementation with hands-on support and accountability." },
  { title: "Growth",       icon: <FaChartLine />,    desc: "Admissions, positioning, and long-term performance scale up." },
];

const gaps = [
  { title: "Academic Gaps",    icon: <FaBookOpen />,  items: ["No structured lesson planning", "No academic calendar", "No learning tracking"] },
  { title: "Teacher Gaps",     icon: <FaUserTie />,   items: ["No continuous training", "No modern teaching exposure", "No accountability system"] },
  { title: "Growth Issues",    icon: <FaChartLine />, items: ["No admission strategy", "No positioning", "No long-term planning"] },
  { title: "Operational Gaps", icon: <FaCogs />,      items: ["No SOPs", "Dependency on individuals", "Inconsistent execution"] },
];

const deliverables = [
  { title: "Academic Transformation",     icon: <FaGraduationCap />, items: ["Structured lesson planning systems", "Teacher capability development", "Academic calendars & tracking"] },
  { title: "Teacher & Student Exposure",  icon: <FaUsers />,         items: ["Expert-led sessions & workshops", "Real-world exposure programs", "Skill & mindset development"] },
  { title: "School Systems & Leadership", icon: <FaSchool />,        items: ["SOPs for consistent execution", "Leadership & principal training", "Annual activity & planning systems"] },
  { title: "Growth & Performance",        icon: <FaBullseye />,      items: ["Monthly performance tracking", "Data-driven insights", "Admission growth strategy"] },
];

const outcomes = [
  { title: "Academic Impact",      items: ["Improved classroom delivery", "Better student understanding", "Consistent academic performance"] },
  { title: "Teacher Growth",       items: ["More confident teaching", "Practical skill improvement", "Accountability in execution"] },
  { title: "Operational Clarity",  items: ["Clear processes across teams", "Less dependency on individuals", "Smooth functioning"] },
  { title: "Business Growth",      items: ["Increase in admissions", "Stronger positioning", "Sustainable growth"] },
];

const keyProjects = [
  { title: "KVS Teachers Training",         stat: "8,000+",  desc: "Trained Kendriya Vidyalaya teachers to make their classrooms smart.", photo: kpKvs },
  { title: "Asia's Largest AI Hackathon",   stat: "30,000+", desc: "Participants engaged in our flagship AI hackathon.", photo: kpHackathon },
  { title: "Navodaya Teachers Training",    stat: "400+",    desc: "Teachers of Navodaya Vidyalaya trained on NEP & NCF guidelines.", photo: kpNavodaya },
  { title: "BEO Training Program",          stat: "95",      desc: "Block Education Officers trained on Medha AI & GenAI tools.", photo: kpBeo },
  { title: "Skill Enhancement Program",     stat: "50,000+", desc: "Students trained on emerging technologies and 21st-century skills.", photo: kpSkill },
];

const partnerLogos = [logoIhfc, logoMnnit, logoPmshri, logoKvs, logoNavodaya, logoEklavya, logoSamagra, partnerScert, partnerSiemt, partnerDosw];

// ─── Reel videos ─────────────────────────────────────────────────────────────
const REEL_VIDEOS = [
  { youtubeId: "gdl2i8RqsOE", cover: reelCover1, name: "Vijender Singh Chauhan", role: "Interviewer · Educator · Keynote Speaker", title: "Transforming Schools Into Model Institutions" },
];

// ─── Single flip phone card ───────────────────────────────────────────────────
const ReelPhone = ({ video, tilt = 0 }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      className="incb-reel__card-phone"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div style={{ transform: `rotate(${tilt}deg)`, transition: "transform 0.3s ease" }}>
        <div style={{ perspective: "1200px" }}>
          <div
            className="incb-reel__phone-inner"
            style={{
              transformStyle: "preserve-3d",
              transition: "transform 0.65s cubic-bezier(0.4,0,0.2,1)",
              transform: hovered ? "rotateY(180deg)" : "rotateY(0deg)",
            }}
          >
            {/* FRONT — cover thumbnail */}
            <div className="incb-reel__phone-face incb-reel__phone-front">
              <div className="incb-reel__phone-notch" />
              <div className="incb-reel__phone-screen">
                <img src={video.cover} alt={video.title} />
              </div>
            </div>
            {/* BACK — video */}
            <div
              className="incb-reel__phone-face incb-reel__phone-back"
              style={{ visibility: hovered ? "visible" : "hidden" }}
            >
              <div className="incb-reel__phone-notch" />
              <div className="incb-reel__phone-screen">
                {hovered && (
                  <iframe
                    src={`https://www.youtube.com/embed/${video.youtubeId}?autoplay=1&rel=0`}
                    title={video.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="incb-reel__phone-info">
        <strong>{video.name}</strong>
        <span>{video.role}</span>
      </div>
    </div>
  );
};

// ─── Component ────────────────────────────────────────────────────────────────

const Incubation = () => {
  const [sharkPlaying, setSharkPlaying] = useState(false);
  const [lightbox, setLightbox] = useState(null); // null = closed, else photo index
  const galleryRef = useRef(null);

  const scrollGallery = (dir) => {
    const el = galleryRef.current;
    if (!el) return;
    const slide = el.querySelector(".incb-orientation__slide");
    const step = slide ? slide.offsetWidth + 16 : el.clientWidth * 0.8;
    el.scrollBy({ left: dir * step * 2, behavior: "smooth" });
  };

  const openLightbox = (i) => setLightbox(i);
  const closeLightbox = () => setLightbox(null);
  const stepLightbox = (dir) =>
    setLightbox((prev) => {
      if (prev === null) return prev;
      const n = orientationPhotos.length;
      return (prev + dir + n) % n;
    });

  // keyboard controls + scroll lock while the lightbox is open
  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      else if (e.key === "ArrowRight") stepLightbox(1);
      else if (e.key === "ArrowLeft") stepLightbox(-1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightbox]);

  useEffect(() => {
    AOS.init({ duration: 500, once: true, offset: 30, easing: 'ease-out' });
    const t = setTimeout(() => AOS.refreshHard(), 100);
    window.scrollTo(0, 0);
    return () => clearTimeout(t);
  }, []);

  const handleApply = () => { window.location.href = "/incubation/register"; };

  return (
    <div className="incb">
      <Helmet>
        <title>School Incubation Program | CoGrad</title>
        <meta name="description" content="CoGrad's School Incubation Program is a structured, multi-year transformation system that turns schools into high-performance institutions through systems, teacher development, and measurable growth." />
      </Helmet>

      <Header />

      {/* ══ HERO ══════════════════════════════════════════════════════════════ */}
      <section className="incb-hero">
        <div className="incb-hero__blob" />
        <div className="incb-hero__dots" />
        <div className="incb-wrap incb-hero__inner">
          <div className="incb-hero__copy" data-aos="fade-up">
            <div className="incb-pill">2026 Cohort · Limited Intake · Selection-Based</div>
            <h1 className="incb-hero__h1">
              School <span className="incb-accent">Incubation</span> Program
            </h1>
            <p className="incb-hero__sub">
              Transform your school into a <strong>high-performance institution</strong> —
              through systems, not advice.
            </p>
            <div className="incb-hero__actions">
              <button className="incb-btn-primary" onClick={handleApply}>
                Apply for Selection <ArrowUpRight size={16} />
              </button>
              <a href="#program" className="incb-btn-ghost">Explore the Program</a>
            </div>
          </div>

          <div className="incb-hero__visual" data-aos="fade-left" data-aos-delay="120">
            <div className="incb-hero__img-wrap">
              <img src={heroPhoto} alt="CoGrad School Incubation Program" />
            </div>
          </div>
        </div>

        <div className="incb-hero__stats" data-aos="fade-up" data-aos-delay="200">
          {[
            { n: impact.students.display,  l: impact.students.label,  icon: <FaUsers /> },
            { n: impact.schools.display,   l: impact.schools.label,   icon: <FaSchool /> },
            { n: impact.incubated.display, l: impact.incubated.label, icon: <FaGraduationCap /> },
            { n: impact.workshops.display, l: impact.workshops.label, icon: <FaChartLine /> },
          ].map((s, i) => (
            <React.Fragment key={s.n}>
              <div className="incb-hero__stat">
                <div className="incb-hero__stat-icon">{s.icon}</div>
                <div className="incb-hero__stat-text">
                  <strong>{s.n}</strong>
                  <span>{s.l}</span>
                </div>
              </div>
              {i < 3 && <div className="incb-hero__stat-sep" />}
            </React.Fragment>
          ))}
        </div>
        <AsOfNote className="asof--center incb-hero__asof" />
      </section>

      {/* ══ TRUST BAR ════════════════════════════════════════════════════════ */}
      <FeaturedIn />

      {/* ══ FOUNDING COHORT — IIIT DELHI EVENT ═══════════════════════════════ */}
      <section className="incb-founding">
        <div className="incb-wrap">
          <div className="incb-founding__card" data-aos="fade-up">
            <div className="incb-founding__pattern" />
            <div className="incb-founding__inner">
              <div className="incb-founding__badge">
                <span>CoGrad</span>
                <i className="incb-founding__badge-div" />
                <span>IIITD Innovation &amp; Incubation Center</span>
              </div>

              <div className="incb-founding__eyebrow">Founding Cohort</div>
              <h2 className="incb-founding__title">
                2026<span className="incb-founding__dash">–</span>27
              </h2>
              <p className="incb-founding__sub">
                Selected school leaders from across India, united to build
                <strong> future-ready institutions.</strong>
              </p>

              <div className="incb-founding__meta">
                <span><FaCalendarAlt /> 27 June 2026</span>
                <i className="incb-founding__meta-dot" />
                <span><FaLandmark /> Senate Hall</span>
                <i className="incb-founding__meta-dot" />
                <span><FaMapMarkerAlt /> IIIT Delhi</span>
              </div>

              <div className="incb-founding__tag">
                Building the next generation of future-ready schools &amp; institutions.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ ORIENTATION PROGRAM — IIIT DELHI ═════════════════════════════════ */}
      <section className="incb-orientation">
        <div className="incb-wrap">
          <div className="incb-section-head" data-aos="fade-up">
            <div className="incb-eyebrow">On-Ground · IIIT Delhi</div>
            <h2>Moments from the <span className="incb-accent">Orientation.</span></h2>
            <p className="incb-section-sub">
              Glimpses from the Founding Cohort orientation at IIIT Delhi's Senate Hall —
              school leaders, educators and the CoGrad team, together.
            </p>
          </div>
          <div className="incb-orientation__carousel" data-aos="fade-up">
            <button
              className="incb-orientation__nav incb-orientation__nav--prev"
              onClick={() => scrollGallery(-1)}
              aria-label="Previous photos"
            >
              <FaChevronLeft />
            </button>

            <div className="incb-orientation__track" ref={galleryRef}>
              {orientationPhotos.map((photo, i) => (
                <div
                  className="incb-orientation__slide"
                  key={i}
                  onClick={() => openLightbox(i)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && openLightbox(i)}
                >
                  <img src={photo} alt={`Orientation program at IIIT Delhi ${i + 1}`} loading="lazy" />
                  <span className="incb-orientation__zoom"><FaSearchPlus /></span>
                </div>
              ))}
            </div>

            <button
              className="incb-orientation__nav incb-orientation__nav--next"
              onClick={() => scrollGallery(1)}
              aria-label="Next photos"
            >
              <FaChevronRight />
            </button>
          </div>
          <p className="incb-orientation__hint">Swipe to explore more moments →</p>
        </div>

        {/* ── Lightbox ── */}
        {lightbox !== null && (
          <div className="incb-lightbox" onClick={closeLightbox}>
            <button className="incb-lightbox__close" onClick={closeLightbox} aria-label="Close">
              <FaTimes />
            </button>
            <button
              className="incb-lightbox__nav incb-lightbox__nav--prev"
              onClick={(e) => { e.stopPropagation(); stepLightbox(-1); }}
              aria-label="Previous"
            >
              <FaChevronLeft />
            </button>

            <figure className="incb-lightbox__stage" onClick={(e) => e.stopPropagation()}>
              <img src={orientationPhotos[lightbox]} alt={`Orientation program at IIIT Delhi ${lightbox + 1}`} />
              <figcaption>{lightbox + 1} / {orientationPhotos.length}</figcaption>
            </figure>

            <button
              className="incb-lightbox__nav incb-lightbox__nav--next"
              onClick={(e) => { e.stopPropagation(); stepLightbox(1); }}
              aria-label="Next"
            >
              <FaChevronRight />
            </button>
          </div>
        )}
      </section>

      {/* ══ SHARK TANK FEATURE ═══════════════════════════════════════════════ */}
      <section className="incb-feature">
        <div className="incb-wrap incb-feature__grid">
          {/* perspective wrapper — AOS on this, NOT on the flip card */}
          <div data-aos="fade-right" style={{ perspective: "1400px", borderRadius: "20px" }}>
            <div
              className="incb-feature__flip"
              onClick={() => !sharkPlaying && setSharkPlaying(true)}
              style={{
                transformStyle: "preserve-3d",
                transition: "transform 0.75s cubic-bezier(0.4,0,0.2,1)",
                transform: sharkPlaying ? "rotateY(180deg)" : "rotateY(0deg)",
                cursor: sharkPlaying ? "default" : "pointer",
              }}
            >
              {/* FRONT — photo */}
              <div className="incb-feature__flip-front">
                <img src={sharkTankPhoto} alt="CoGrad on Shark Tank India" />
                <div className="incb-feature__thumb-overlay">
                  <div className="incb-feature__play-btn">
                    <Play size={28} fill="white" color="white" />
                  </div>
                  <span className="incb-feature__thumb-label">Watch on Shark Tank India</span>
                </div>
              </div>
              {/* BACK — video */}
              <div className="incb-feature__flip-back">
                <iframe
                  src={sharkPlaying ? "https://www.youtube.com/embed/jMkCnEDCJo8?autoplay=1&rel=0" : "about:blank"}
                  title="CoGrad on Shark Tank India"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          </div>
          <div className="incb-feature__copy" data-aos="fade-left">
            <div className="incb-eyebrow">National Recognition</div>
            <h2>Featured on <span className="incb-accent">Shark Tank India.</span></h2>
            <p>
              CoGrad's mission to revamp rural education through affordable schools and
              AI-powered teaching tools has been spotlighted on national platforms — with
              backing from leading institutions, advisors and investors.
            </p>
            <div className="incb-feature__pills">
              {["Shark Tank India", "YourStory", "Hindustan Times", "Times of India", "Entrepreneur India"].map((n) => (
                <span key={n}>{n}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══ VIDEO REEL ═══════════════════════════════════════════════════════ */}
      <section className="incb-reel">
        <div className="incb-wrap incb-reel__grid">

          {/* Left — phone */}
          <div className="incb-reel__phone-col" data-aos="fade-right">
            <ReelPhone video={REEL_VIDEOS[0]} tilt={0} />
          </div>

          {/* Right — content */}
          <div className="incb-reel__copy" data-aos="fade-left">
            <div className="incb-eyebrow">Educator Voices</div>
            <h2>Voices from educators who are <span className="incb-reel__hl">reshaping India's classrooms</span></h2>
            <p className="incb-reel__sub">Real stories from school leaders and educators who have experienced CoGrad's transformation firsthand.</p>

            <div className="incb-reel__stats">
              <div className="incb-reel__stat">
                <strong>26+</strong>
                <span>Schools Incubated</span>
              </div>
              <div className="incb-reel__stat">
                <strong>{impact.students.short}</strong>
                <span>Students Impacted</span>
              </div>
              <div className="incb-reel__stat">
                <strong>5K+</strong>
                <span>Workshops Conducted</span>
              </div>
            </div>

            <p className="incb-reel__hint">Hover on the phone to watch their story</p>
          </div>

        </div>
      </section>

      {/* ══ EXECUTIVE SUMMARY ════════════════════════════════════════════════ */}
      <section className="incb-exec">
        <div className="incb-wrap incb-exec__grid">
          <div className="incb-exec__copy" data-aos="fade-up">
            <div className="incb-eyebrow">Executive Summary</div>
            <h2>We don't offer advice.<br />We <span className="incb-accent">build systems</span> inside schools.</h2>
            <p>
              CoGrad is a social edtech startup transforming rural and aspirational education
              across India through affordable schools, AI-driven teaching tools, and
              emerging-tech training. Featured on Shark Tank India, CoGrad empowers students
              and teachers with quality education, Medha AI, and nationwide programs aligned
              with NEP 2020 — impacting over {impact.students.display} learners.
            </p>
            <ul className="incb-checklist">
              <li><FaCheckCircle /><span>Partnered with {impact.schools.display} schools across rural India.</span></li>
              <li><FaCheckCircle /><span>Quality education to 20,000+ students at ₹500/month.</span></li>
              <li><FaCheckCircle /><span>Built India's first Teacher Assistive AI Platform — Medha AI.</span></li>
            </ul>
          </div>
          <div className="incb-exec__visual" data-aos="fade-left">
            <div className="incb-exec__img">
              <img src={muralPhoto} alt="CoGrad at a revamped rural school" />
            </div>
            <div className="incb-exec__quote">
              <p>"India is not lacking schools. It is lacking <strong>great schools</strong>."</p>
              <span>— The CoGrad Thesis</span>
            </div>
          </div>
        </div>
      </section>

      {/* ══ PROBLEM ══════════════════════════════════════════════════════════ */}
      <section className="incb-problem">
        <div className="incb-wrap incb-problem__layout">

          {/* Left — photo with top badge + bottom stats overlay */}
          <div className="incb-problem__photo-card" data-aos="fade-right">
            <img src={auditoriumPhoto} alt="" />
            <div className="incb-problem__photo-veil" />
            <div className="incb-problem__photo-eyebrow">
              <div className="incb-eyebrow incb-eyebrow--light">The Problem</div>
            </div>
            <div className="incb-problem__photo-content">
              <p className="incb-problem__photo-quote">
                "Most schools want to improve.<br />Almost none have a system to."
              </p>
              <div className="incb-problem__bignums">
                <div className="incb-problem__bignum-item">
                  <span className="incb-problem__bignum">15L+</span>
                  <span className="incb-problem__biglabel">Schools in India</span>
                </div>
                <div className="incb-problem__bignum-sep" />
                <div className="incb-problem__bignum-item">
                  <span className="incb-problem__bignum incb-problem__bignum--amber">70%</span>
                  <span className="incb-problem__biglabel">Struggling with outcomes</span>
                  <div className="incb-problem__bignum-bar"><div /></div>
                </div>
              </div>
            </div>
          </div>

          {/* Right — heading + 2×2 gap grid */}
          <div className="incb-problem__right" data-aos="fade-left">
            <h2 className="incb-problem__heading">
              India has schools.<br />Not <span className="incb-accent">great</span> schools.
            </h2>
            <p className="incb-problem__sub">
              Systems break before results do. The problem is not intent — it's the absence of a system.
            </p>
            <p className="incb-problem__right-label">4 Critical Gaps Holding Schools Back</p>
            <div className="incb-problem__gap-grid">
              {gaps.map((g, i) => (
                <div className="incb-problem__gap-card" key={g.title}>
                  <div className="incb-problem__gap-card-head">
                    <div className="incb-problem__gap-icon">{g.icon}</div>
                    <h3>{g.title}</h3>
                  </div>
                  <div className="incb-problem__gap-pills">
                    {g.items.map((it) => <span key={it}>{it}</span>)}
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* ══ THE PROGRAM ══════════════════════════════════════════════════════ */}
      <section id="program" className="incb-program">
        <div className="incb-wrap incb-program__layout">

          {/* Left — heading + 3 key pillars */}
          <div className="incb-program__left" data-aos="fade-right">
            <div className="incb-eyebrow">The Program</div>
            <h2 className="incb-program__h2">
              A structured system.<br />Not a <span className="incb-accent">workshop.</span>
            </h2>
            <p className="incb-program__sub">
              CoGrad embeds inside your school and rebuilds it end-to-end — academics, operations, leadership, and growth.
            </p>
            <div className="incb-program__pillars">
              {[
                { h: "Limited Intake",       p: "Only select schools onboarded each cohort for deep execution." },
                { h: "Long-Term Model",      p: "A structured multi-year journey — not a short-term fix." },
                { h: "Serious Schools Only", p: "For institutions committed to real, measurable transformation." },
              ].map((pl) => (
                <div className="incb-program__pillar" key={pl.h}>
                  <strong>{pl.h}</strong>
                  <span>{pl.p}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right — vertical timeline */}
          <div className="incb-program__right" data-aos="fade-left">
            <p className="incb-program__tl-label">How it works — in 5 steps</p>
            <div className="incb-program__timeline">
              {programSteps.map((step, i) => (
                <div className="incb-program__tl-item" key={step.title}>
                  <div className="incb-program__tl-track">
                    <div className="incb-program__tl-dot">
                      <span>{String(i + 1).padStart(2, "0")}</span>
                    </div>
                    {i < programSteps.length - 1 && <div className="incb-program__tl-line" />}
                  </div>
                  <div className="incb-program__tl-content">
                    <div className="incb-program__tl-icon">{step.icon}</div>
                    <div>
                      <h4>{step.title}</h4>
                      <p>{step.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="incb-program__notice">
              Selection is limited. Entry is <strong>not guaranteed.</strong>
            </div>
          </div>

        </div>
      </section>

      {/* ══ DELIVERABLES ═════════════════════════════════════════════════════ */}
      <section className="incb-deliverables">
        <div className="incb-wrap incb-deliverables__layout">

          {/* Left — editorial photo */}
          <div className="incb-deliverables__photo" data-aos="fade-right">
            <img src={iitBombayPhoto} alt="CoGrad School Incubation Program orientation at IIIT Delhi" />
            <div className="incb-deliverables__photo-veil" />
            <div className="incb-deliverables__photo-tag">
              <strong>Where the transformation begins</strong>
              <span>School leaders onboarded through the Founding Cohort at IIIT Delhi.</span>
            </div>
          </div>

          {/* Right — heading + row list with pills */}
          <div className="incb-deliverables__right" data-aos="fade-left">
            <div className="incb-eyebrow">What Schools Actually Get</div>
            <h2 className="incb-deliverables__h2">
              A complete <span className="incb-accent">execution system</span><br />delivered end-to-end
            </h2>
            <p className="incb-deliverables__sub">
              Four pillars deployed together — improving learning, teachers, operations, and growth.
            </p>
            <div className="incb-deliverables__rows">
              {deliverables.map((d) => (
                <div className="incb-del-row" key={d.title}>
                  <div className="incb-del-row__icon">{d.icon}</div>
                  <div className="incb-del-row__body">
                    <h4>{d.title}</h4>
                    <div className="incb-del-row__pills">
                      {d.items.map((it) => <span key={it}>{it}</span>)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* ══ OUTCOMES ═════════════════════════════════════════════════════════ */}
      <section className="incb-outcomes">
        <div className="incb-wrap incb-outcomes__layout">

          {/* Left — photo with glass-morphism stat bar */}
          <div className="incb-outcomes__photo" data-aos="fade-right">
            <img src={schoolStudentsPhoto} alt="CoGrad classroom session" />
            <div className="incb-outcomes__photo-veil" />
            <div className="incb-outcomes__stats-bar">
              {[
                { n: impact.students.short,  l: "Students"  },
                { n: impact.schools.short,   l: "Schools"   },
                { n: impact.incubated.short, l: "Incubated" },
              ].map((s, i) => (
                <React.Fragment key={s.n}>
                  {i > 0 && <div className="incb-outcomes__bar-sep" />}
                  <div className="incb-outcomes__bar-stat">
                    <strong>{s.n}</strong>
                    <span>{s.l}</span>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Right — heading + outcome rows with pills */}
          <div className="incb-outcomes__right" data-aos="fade-left">
            <div className="incb-eyebrow">The Outcomes You Will See</div>
            <h2 className="incb-outcomes__h2">
              Real improvements across<br /><span className="incb-accent">every dimension.</span>
            </h2>
            <p className="incb-outcomes__sub">
              When systems are right, outcomes follow. Schools in the CoGrad program see measurable impact across all areas.
            </p>
            <div className="incb-outcomes__rows">
              {outcomes.map((o, i) => (
                <div className="incb-outcome-row" key={o.title}>
                  <div className="incb-outcome-row__num">0{i + 1}</div>
                  <div className="incb-outcome-row__body">
                    <h4>{o.title}</h4>
                    <div className="incb-outcome-row__pills">
                      {o.items.map((it) => <span key={it}>{it}</span>)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* ══ BOARD ════════════════════════════════════════════════════════════ */}
      <section className="incb-board">
        <div className="incb-wrap">
          <div className="incb-section-head" data-aos="fade-up">
            <div className="incb-eyebrow">Education Board</div>
            <h2>Guided by <span className="incb-accent">India's leading educators.</span></h2>
            <p className="incb-section-sub">A board of academic, strategic and policy experts shaping the program.</p>
          </div>
          <div className="incb-board__grid">
            {boardMembers.map((m, i) => (
              <div className="incb-board-card" key={m.name} data-aos="fade-up" data-aos-delay={(i % 4) * 70}>
                <div className="incb-board-card__avatar">
                  <img src={m.photo} alt={m.name} />
                </div>
                <h4>{m.name}</h4>
                <span className="incb-board-card__role">{m.role}</span>
                <span className="incb-board-card__org">{m.org}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ HOW IT WORKS ═════════════════════════════════════════════════════ */}
      <section className="incb-how">
        <div className="incb-wrap incb-how__grid">
          <div className="incb-how__photo" data-aos="fade-right">
            <img src={rashtrapatiKidsPhoto} alt="CoGrad School Incubation Program orientation" />
          </div>
          <div className="incb-how__copy" data-aos="fade-left">
            <div className="incb-eyebrow">How CoGrad Works</div>
            <h2>Built for real schools. Designed for <span className="incb-accent">long-term impact.</span></h2>
            <p>
              We don't deliver workshops or short-term fixes. CoGrad embeds inside schools
              and rebuilds them end-to-end — academics, operations, leadership, and growth —
              until results are visible and the systems run themselves.
            </p>
            <ul className="incb-checklist">
              <li><FaCheckCircle /><span>System-driven approach, not random efforts</span></li>
              <li><FaCheckCircle /><span>Continuous teacher development &amp; classroom improvement</span></li>
              <li><FaCheckCircle /><span>Full-school transformation aligned with NEP 2020</span></li>
              <li><FaCheckCircle /><span>Performance tracking with measurable outcomes</span></li>
            </ul>
          </div>
        </div>
      </section>

      {/* ══ KEY PROJECTS ═════════════════════════════════════════════════════ */}
      <section className="incb-projects">
        <div className="incb-wrap">
          <div className="incb-section-head" data-aos="fade-up">
            <div className="incb-eyebrow">Key Projects &amp; Initiatives</div>
            <h2>Track record built on <span className="incb-accent">real implementation.</span></h2>
          </div>
          <div className="incb-projects__grid">
            {keyProjects.map((p, i) => (
              <div className="incb-project-card" key={p.title} data-aos="fade-up" data-aos-delay={i * 70}>
                <div className="incb-project-card__photo">
                  <img src={p.photo} alt={p.title} />
                </div>
                <div className="incb-project-card__body">
                  <div className="incb-project-card__stat">{p.stat}</div>
                  <h4>{p.title}</h4>
                  <p>{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ AWARDS ═══════════════════════════════════════════════════════════ */}
      <section className="incb-awards">
        <div className="incb-wrap">
          <div className="incb-section-head" data-aos="fade-up">
            <div className="incb-eyebrow">Awards &amp; Recognitions</div>
            <h2>Recognized for <span className="incb-accent">social impact &amp; rural skilling.</span></h2>
          </div>
          <div className="incb-awards__grid">
            {[
              { photo: awardSocialImpact, title: "Top Social Impact Entrepreneur India",  by: "by Former Chief Justice of India" },
              { photo: awardRuralSkill,   title: "Best Emerging Rural Skilling Platform", by: "by Entrepreneur India" },
              { photo: awardMnnit,        title: "MoU with MNNIT Allahabad",              by: "Strategic academic partnership" },
            ].map((a, i) => (
              <div className="incb-award-card" key={a.title} data-aos="fade-up" data-aos-delay={i * 80}>
                <div className="incb-award-card__img">
                  <img src={a.photo} alt={a.title} />
                </div>
                <div className="incb-award-card__body">
                  <FaTrophy className="incb-award-card__trophy" />
                  <div>
                    <strong>{a.title}</strong>
                    <span>{a.by}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ ASSOCIATIONS ═════════════════════════════════════════════════════ */}
      <section className="incb-assoc">
        <div className="incb-wrap">
          <div className="incb-section-head" data-aos="fade-up">
            <div className="incb-eyebrow">Associated Boards &amp; Institutes</div>
            <h2>Backed by India's most <span className="incb-accent">credible institutions.</span></h2>
          </div>
          <div className="incb-assoc__grid">
            {partnerLogos.map((logo, i) => (
              <div className="incb-assoc__cell" key={i} data-aos="fade-up" data-aos-delay={(i % 5) * 50}>
                <img src={logo} alt="Partner institution" />
              </div>
            ))}
          </div>
        </div>
      </section>



      {/* ══ CTA ══════════════════════════════════════════════════════════════ */}
      <section className="incb-cta">
        <div className="incb-wrap">
          <div className="incb-cta__card" data-aos="fade-up" style={{ "--cta-bg": `url(${boardroomPhoto})` }}>
            <div className="incb-cta__bg" />
            <div className="incb-cta__overlay" />

            <div className="incb-cta__inner">
              {/* Left */}
              <div className="incb-cta__left">
                <div className="incb-eyebrow incb-eyebrow--light">Apply for the 2026 Cohort</div>
                <h2>Turn your school into a<br /><span>high-performance institution.</span></h2>
                <p>Selection is limited. Schools serious about scale apply early — seats fill fast.</p>
                <div className="incb-cta__actions">
                  <button className="incb-btn-white" onClick={handleApply}>
                    Apply for Selection <ArrowUpRight size={16} />
                  </button>
                  <a href="/contact-us" className="incb-btn-ghost-white">Talk to our team</a>
                </div>
              </div>

              {/* Right — cohort detail card */}
              <div className="incb-cta__details-card">
                <div className="incb-cta__details-label">2026 Cohort — What You Get</div>
                <ul className="incb-cta__details-list">
                  {[
                    "12-month structured program",
                    "Expert advisory board access",
                    "Medha AI + full app suite",
                    "Teacher & student training",
                    "Live analytics dashboard",
                    "Limited to 30 schools only",
                  ].map((item) => (
                    <li key={item}>
                      <FaCheckCircle />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="incb-cta__details-note">
                  Applications reviewed on a rolling basis
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Incubation;
